import { z } from 'zod';

const labels = z
  .object({
    fi: z.string().nullable(),
    en: z.string().nullable(),
    sv: z.string().nullable(),
    es: z.string().nullable(),
  })
  .strict();

const windowMonths = z.union([z.literal(3), z.literal(12), z.literal(24), z.literal(36)]);

const price = z.string().regex(/^-?\d+(\.\d+)?$/);

const reportPoint = z
  .object({
    week_code: z.string().min(1),
    week_start_date: z.iso.date(),
    price_eur_per_m3: price.nullable(),
    source_dataset: z.string().min(1).nullable(),
    availability: z.enum(['available', 'unavailable']),
    reason_code: z.literal('no_observation').nullable(),
  })
  .strict();

const seriesSummary = z
  .object({
    start_week_code: z.string().min(1).nullable(),
    start_date: z.iso.date().nullable(),
    start_price_eur_per_m3: price.nullable(),
    latest_week_code: z.string().min(1).nullable(),
    latest_date: z.iso.date().nullable(),
    latest_price_eur_per_m3: price.nullable(),
    absolute_change_eur_per_m3: price.nullable(),
    relative_change_percent: price.nullable(),
    relative_change_unavailable_reason: z
      .enum(['no_series_observations', 'zero_start_price'])
      .nullable(),
  })
  .strict();

export const timberPriceReportSnapshotSchema = z
  .object({
    revision: z.literal('timber-price-report.v1'),
    context_ref: z.string().min(1),
    snapshot_ref: z.string().min(1),
    dataset_revision: z.string().min(1),
    series_revision: z.string().min(1),
    policy_revision: z.string().min(1),
    status: z.enum(['available', 'partial', 'stale', 'unavailable']),
    availability_reason_codes: z.array(
      z.enum([
        'no_series_observations',
        'missing_series_observations',
        'missing_latest_assortment_prices',
        'regional_prices_carried_forward',
        'insufficient_history',
        'source_stale',
        'source_never_synced',
        'source_sync_error',
      ]),
    ),
    context: z
      .object({
        region_code: z.string().min(1),
        region_labels: labels,
        harvest_type_code: z.string().min(1),
        harvest_type_labels: labels,
        assortment_code: z.string().min(1),
        assortment_labels: labels,
        date_from: z.iso.date(),
        date_to: z.iso.date(),
        window_months: windowMonths,
        unit: z.literal('EUR/m3'),
      })
      .strict(),
    options: z
      .object({
        regions: z.array(
          z
            .object({
              code: z.string().min(1),
              is_national: z.boolean(),
              labels,
            })
            .strict(),
        ),
        harvest_types: z.array(
          z
            .object({
              code: z.string().min(1),
              labels,
            })
            .strict(),
        ),
        assortments: z.array(
          z
            .object({
              code: z.string().min(1),
              labels,
              species_group: z.string().min(1),
              product_type: z.string().min(1),
            })
            .strict(),
        ),
        window_months: z.array(windowMonths),
      })
      .strict(),
    dataset: z.string().min(1).nullable(),
    dataset_ids: z.array(z.string().min(1)),
    observed_at: z.iso.date().nullable(),
    retrieved_at: z.iso.datetime({ offset: true }).nullable(),
    series: z.array(reportPoint),
    summary: seriesSummary,
    assortment_series: z.array(
      z
        .object({
          assortment_code: z.string().min(1),
          assortment_labels: labels,
          species_group: z.string().min(1),
          product_type: z.string().min(1),
          series_revision: z.string().min(1),
          series: z.array(reportPoint),
          summary: seriesSummary,
        })
        .strict(),
    ),
    history: z
      .object({
        band: z.enum(['near_high', 'average', 'below_average', 'insufficient_history']),
        percentile: z.number().min(0).max(100).nullable(),
        basis_weeks: z.number().int().nonnegative(),
        window_months: windowMonths,
        latest_week_code: z.string().min(1).nullable(),
        availability: z.enum(['available', 'unavailable']),
        reason_code: z.enum(['insufficient_history', 'no_series_observations']).nullable(),
      })
      .strict(),
    carried_forward_prices: z.array(
      z.object({
        sale_basis: z.enum(['standing', 'roadside']),
        price_key: z.string().min(1),
        observation_week: z.string().regex(/^\d{4}W\d{2}$/),
        observation_date: z.iso.date(),
        reason_code: z.literal('no_newer_regional_observation'),
      }).strict(),
    ),
    latest_assortment_rows: z.array(
      z
        .object({
          assortment_code: z.string().min(1),
          assortment_labels: labels,
          species_group: z.string().min(1),
          product_type: z.string().min(1),
          week_code: z.string().min(1).nullable(),
          week_start_date: z.iso.date().nullable(),
          price_eur_per_m3: price.nullable(),
          availability: z.enum(['available', 'unavailable']),
          reason_code: z.literal('no_observation_for_latest_week').nullable(),
        })
        .strict(),
    ),
    freshness: z
      .object({
        latest_sync_at: z.iso.datetime({ offset: true }).nullable(),
        latest_week_code: z.string().min(1).nullable(),
        data_age_hours: z.number().nonnegative().nullable(),
        sync_status: z.enum(['ok', 'stale', 'never_synced', 'error']),
        rows_in_database: z.number().int().nonnegative(),
      })
      .strict(),
  })
  .strict();

export type TimberPriceReportSnapshot = z.infer<typeof timberPriceReportSnapshotSchema>;
export type TimberPriceReportPoint = TimberPriceReportSnapshot['series'][number];
export type TimberPriceLatestAssortmentRow =
  TimberPriceReportSnapshot['latest_assortment_rows'][number];
export type TimberPriceLocalizedLabels = TimberPriceReportSnapshot['context']['region_labels'];
